import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="pt-20 min-h-screen bg-[#F1FAEE]">
      {/* Header */}
      <section className="bg-[#14213D] py-16" data-testid="privacy-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-[#00B4D8]" />
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">
              Privacy Policy
            </h1>
          </div>
          <p className="text-gray-400">Last updated: January 2024</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-sm"
          >
            <div className="prose max-w-none text-gray-700 space-y-6">
              <p>
                Medical Access Inc ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                information when you visit our website accessmedicalgroup.online or use our services.
              </p>

              <h2 className="font-heading text-2xl font-semibold text-[#14213D] mt-8 mb-4">
                Information We Collect
              </h2>
              <p>
                We collect information that you provide directly to us, such as when you:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Fill out contact forms or request appointments</li>
                <li>Subscribe to our newsletter</li>
                <li>Apply for a position with Medical Access Inc</li>
                <li>Communicate with our healthcare providers</li>
                <li>Access patient portal services</li>
              </ul>

              <h2 className="font-heading text-2xl font-semibold text-[#14213D] mt-8 mb-4">
                Protected Health Information (PHI)
              </h2>
              <p>
                Medical Access Inc complies with the Health Insurance Portability and Accountability 
                Act (HIPAA). We maintain the confidentiality of your Protected Health Information 
                and only use or disclose it as permitted by law.
              </p>

              <h2 className="font-heading text-2xl font-semibold text-[#14213D] mt-8 mb-4">
                How We Use Your Information
              </h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide healthcare services and connect you with providers</li>
                <li>Communicate with you about appointments and care</li>
                <li>Process job applications and inquiries</li>
                <li>Send newsletters and health information (with consent)</li>
                <li>Comply with legal and regulatory requirements</li>
              </ul>

              <h2 className="font-heading text-2xl font-semibold text-[#14213D] mt-8 mb-4">
                Information Sharing
              </h2>
              <p>
                We do not sell your personal information. We may share information with healthcare 
                providers in our network to facilitate your care, with service providers who assist 
                our operations, or when required by law.
              </p>

              <h2 className="font-heading text-2xl font-semibold text-[#14213D] mt-8 mb-4">
                Data Security
              </h2>
              <p>
                We implement appropriate technical and organizational measures to protect your 
                personal information. However, no method of transmission over the internet is 
                100% secure.
              </p>

              <h2 className="font-heading text-2xl font-semibold text-[#14213D] mt-8 mb-4">
                Your Rights
              </h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access your personal information</li>
                <li>Request corrections to your data</li>
                <li>Request deletion of your data (subject to legal requirements)</li>
                <li>Opt-out of marketing communications</li>
                <li>File a complaint with regulatory authorities</li>
              </ul>

              <h2 className="font-heading text-2xl font-semibold text-[#14213D] mt-8 mb-4">
                Contact Us
              </h2>
              <p>
                If you have questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="bg-[#F1FAEE] rounded-lg p-6 mt-4">
                <p className="font-semibold text-[#14213D]">Medical Access Inc</p>
                <p>16475 N Dallas Pky, Suite 745</p>
                <p>Addison, TX 75001</p>
                <p className="mt-2">Phone: (866) 841-2149</p>
                <p>Email: <a href="mailto:privacy@accessmedicalgroup.online" className="text-[#00B4D8] hover:underline">privacy@accessmedicalgroup.online</a></p>
                <p>Website: <a href="https://accessmedicalgroup.online" className="text-[#00B4D8] hover:underline">accessmedicalgroup.online</a></p>
              </div>
            </div>
          </motion.article>
        </div>
      </section>
    </div>
  );
}
